// ink-component
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { CSSDisplayType } from '../../helpers/constants/cssPropTypes';
import Icon from '../Icon/Icon';

const REDACTIONS = {
  all: /./g,
  numbers: /\d/g,
  letters: /[a-zA-Z]/g,
};

type RedactionType = 'all' | 'numbers' | 'letters';

export interface SensitiveDataProps {
  /** Information to be redacted, must be a string */
  children: string;
  /** Whether or not to display an eye Icon */
  icon: boolean;
  /** Passed to CSS display property */
  display: CSSDisplayType;
  /** Character types to be redacted */
  redaction: RedactionType;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string;
}

const SensitiveDataWrapper = styled.span<Pick<SensitiveDataProps, 'display'>>`
  cursor: pointer;
  display: ${props => props.display};
`;

/**
 * @purpose Hides sensitive user information
 * @categories accessory
 * @status beta
 */
function SensitiveData(props: SensitiveDataProps) {
  const { children, icon, display, redaction, 'data-testid': dataTestId, ...otherProps } = props;

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;

    const onEnter = () => setVisible(true);
    const onExit = () => setVisible(false);

    element.addEventListener('mouseenter', onEnter);
    element.addEventListener('mouseleave', onExit);

    return () => {
      element.removeEventListener('mouseenter', onEnter);
      element.removeEventListener('mouseleave', onExit);
    };
  }, [ref]);

  if (children === '') return null;

  const redactedStr = children.replace(REDACTIONS[redaction], '*');

  return (
    <SensitiveDataWrapper data-private ref={ref} data-testid={dataTestId} display={display} {...otherProps}>
      {icon && <Icon type={visible || children === redactedStr ? 'preview' : 'hide'} />}{' '}
      {visible ? children : redactedStr}
    </SensitiveDataWrapper>
  );
}

SensitiveData.defaultProps = {
  display: 'inline',
  icon: false,
  children: '',
  redaction: 'all',
};

SensitiveData.displayName = 'SensitiveData';

export default SensitiveData;
